import {
  useState, useEffect, useMemo,
  Dispatch,
  SetStateAction,
  CSSProperties,
  ReactNode,
  ReactElement,
} from 'react';
import { toast } from 'react-toastify';
import { Box, Flex } from 'reflexbox';
import { InputSearch } from '../InputSearch';
import { colors } from '../../styles/colors';
import {
  PaginationContainer,
  Page,
  ButtonPage,
  TableContainer,
  TableHead,
  HeaderRow,
  HeaderCell,
  HeaderCellOrder,
  ChevronTop,
  ChevronBottom,
  ChevronLeft,
  ChevronRight,
  TableBody,
  Row,
  DataCell,
  CheckboxStyle,
  ResponsiveTable,
  ActionsContainer,
  ActionButton,
} from './styles';
import { useTranslation } from 'react-i18next';
import { getUserProfile } from '../../helpers/userProfile';
import {
  Checkbox, ModalWindow, Button,
} from '../index';
import { useStateVar } from '../../helpers/useStateVar';
import { SearchInput, Label } from '../../pages/Analysis/styles';
import SelectSearch, { fuzzySearch } from 'react-select-search';
import { useParams } from 'react-router-dom';
import { ApiResps, apiCall } from '../../providers';
import { NewPagination } from '../NewPagination';
import { Edit5Icon, Trash3Icon } from 'icons';

function sortNulls(aIsNull, bIsNull, sortOrder) {
  if (sortOrder === '+') {
    if (aIsNull && !bIsNull) return 5;
    if (!aIsNull && bIsNull) return -5;
  }
  if (sortOrder === '-') {
    if (aIsNull && !bIsNull) return -5;
    if (!aIsNull && bIsNull) return 5;
  }

  return 0;
}

export function dynamicSort(property, sortOrder) {
  return function (a, b) {
    const getVal = (row) => (typeof property.accessor === 'function' ? property.accessor(row) : row[property.accessor]);
    const aa = getVal(a);
    const bb = getVal(b);
    // equal items sort equally
    if (aa === bb) {
      return 0;
    }
    // nulls
    const aIsNull = aa == null;
    const bIsNull = bb == null;
    const nullCompare = sortNulls(aIsNull, bIsNull, sortOrder);
    if (nullCompare !== 0) return nullCompare;
    // otherwise, if we're ascending, lowest sorts first
    if (sortOrder === '+') {
      return aa < bb ? -1 : 1;
    }
    // if descending, highest sorts first
    return aa < bb ? 1 : -1;
  };
}

type NewTableProps = {
  columns: {
    name: string;
    value: string;
    accessor?: string;
    checkable?: boolean;
    sortable?: boolean;
    width?: CSSProperties['width'];
    textAlign?: string;
    render?: (row: any) => ReactNode;
  }[];
  data: any;
  pageSize: number;
  noSearchBar?: boolean;
  extraBtns?: JSX.Element[];
  hideEmptyTable?: boolean;
  checkBox?: boolean;
  multipleConfig?: boolean;
  deleteUnitsOpen?: boolean;
  onDeleteUnits?: (units: { UNIT_ID: number, checked: boolean }[]) => Promise<void>;
  onCloseDeleteUnits?: () => void;
  verifyMobile?: boolean;
  supervisors?: {
    USER: string
    LAST_ACCESS: string
    NOME: string
    SOBRENOME: string
    perfil: string
    PERMS_U: string
    CLIENT_BIND: number
    FULLNAME: string
    clientName: string
    clientIds: number[]
    clientNames: string[]
    unitIds?: number[]
    RG: string
    COMMENTS: string
    PICTURE: string
    CITY_ID: string
    CITY_NAME: string
    STATE_ID: string
    STATE_NAME: string
    IS_ACTIVE: string
  }[],
  renderUnits?: () => void;
  keySearch?: string;
  containerStyle?: CSSProperties;
  tableStyle?: CSSProperties;
  styledPagination?: boolean;
  order?: string;
  setOrder?: Dispatch<SetStateAction<''>>;
  setSort?: Dispatch<SetStateAction<string>>;
  sort?: string;
  actions?: {
    onEdit?: (row: any) => void,
    onDelete?: (row: any) => void,
  };
  variant?: 'striped';
  maxPages?: number;
  changePage?: (newPage: number) => void;
  page?: number;
  dataForPage?: boolean;
  selectAllCheckBox?: (selected: boolean, event) => void;
  isAllSelected?: boolean;
}

type ActionProps<TypeRow> = {
  row: TypeRow,
  onEdit?: (row: TypeRow) => void,
  onDelete?: (row: TypeRow) => void
}

export const NewTable = ({
  columns, data, pageSize, noSearchBar, extraBtns, hideEmptyTable, checkBox, multipleConfig, supervisors, renderUnits, keySearch, deleteUnitsOpen, onDeleteUnits, onCloseDeleteUnits, verifyMobile, containerStyle, tableStyle, styledPagination, order = '', setOrder, sort = '+', setSort, actions, maxPages,
  dataForPage,
  page,
  changePage,
  variant,
  selectAllCheckBox,
  isAllSelected,
}: NewTableProps): JSX.Element => {
  const { clientId } = useParams<{ clientId: string }>();
  const [profile] = useState(getUserProfile);
  const [orderColum, setOrderColumn] = useState(order);
  const [sortOrder, setSortOrder] = useState(sort);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState('');
  const [filteredRows, setFilteredRows] = useState(data);
  const [state, render, setState] = useStateVar(() => {
    const state = {
      selectedData: [] as {
        UNIT_ID: number,
        SUPERVISOR_ID: string,
      }[],
      supervisors: [] as { USER: string }[],
      selectOption: '' as string,
      checkedItems: [] as number[],
    };
    return state;
  });
  const { t } = useTranslation();

  useEffect(() => {
    setFilteredRows(data);
    setCurrentPage(page ?? 1);
  }, [data]);

  function toogleChecked(item) {
    setFilteredRows(filteredRows.map((doc) => {
      if (item.UNIT_ID === doc.UNIT_ID) {
        return ({ ...doc, checked: !doc.checked });
      }
      return doc;
    }));
    if (!state.checkedItems.includes(item.UNIT_ID)) {
      setState({ checkedItems: [...state.checkedItems, item.UNIT_ID] });
    } else {
      setState({ checkedItems: state.checkedItems.filter((id) => id !== item.UNIT_ID) });
    }
  }

  function AllChecked(ischecked) {
    setFilteredRows(filteredRows.map((item) => ({ ...item, checked: ischecked })));
    if (ischecked) {
      const notIncludes = [...filteredRows.filter((item) => !state.checkedItems.includes(item.UNIT_ID))];
      const idsNotIncludes = notIncludes.map((item) => item.UNIT_ID);
      setState({ checkedItems: [...state.checkedItems, ...idsNotIncludes] });
    } else {
      const includes = filteredRows.map((item) => item.UNIT_ID);
      const filter = state.checkedItems.filter((item) => !includes.some((id) => id === item));
      setState({ checkedItems: filter });
    }
  }

  function decideData() {
    if (dataForPage) return data;
    return filteredRows.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  }

  const getDataPerPage = useMemo(() => decideData(), [filteredRows, currentPage, pageSize]);

  const chevron = (column: { name: string }) => {
    if (orderColum === column.name) {
      if (sortOrder === '+') {
        return <ChevronTop />;
      }

      return <ChevronBottom />;
    }
  };

  const handleSetOrderColumn = (column) => {
    let newSortOrder = '+';
    if (orderColum === column.name && sortOrder === '+') {
      newSortOrder = '-';
    }

    let sortedRows;
    if (orderColum === column.name && sortOrder === newSortOrder) {
      setOrderColumn('');
      setSortOrder('');
    } else {
      sortedRows = [...filteredRows].sort(dynamicSort(column, newSortOrder));
      setOrderColumn(column.name);
      if (setOrder) setOrder(column.name);
      setSortOrder(newSortOrder);
      if (setSort) setSort(newSortOrder);
    }

    if (currentPage !== 1) {
      setCurrentPage(1);
    }
    setFilteredRows(sortedRows);
  };

  function getObjects(obj, val) {
    const objects: any[] = [];
    for (const i in obj) {
      if (!obj.hasOwnProperty(i)) continue;
      if (obj[i] && typeof obj[i] === 'object') {
        continue;
      } else if (obj[i] && obj[i].toString().toLowerCase().includes(val.toString().toLowerCase())) {
        if (objects.lastIndexOf(obj) === -1) {
          objects.push(obj);
        }
      }
    }
    return objects;
  }

  const onSearch = (e) => {
    setSearchText(e.target.value);

    if (e.target.value) {
      setFilteredRows(data.filter((row) => getObjects(row, e.target.value).length));
    } else {
      setFilteredRows(data);
    }
    setOrderColumn('+');
    setSortOrder('');
    setCurrentPage(1);
  };

  const onPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const onNextPage = () => {
    if (currentPage * pageSize < filteredRows.length) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleChangePage = (newPage) => {
    if (newPage >= 1 && newPage <= Math.ceil((maxPages ?? filteredRows.length) / pageSize)) {
      if (changePage) changePage(newPage);
      setCurrentPage(newPage);
    }
  };

  function renderHeaderCell({
    column,
    index,
    variant,
    handleSetOrderColumn,
    chevron,
  }: {
    column: any;
    index: number;
    variant: any;
    handleSetOrderColumn: (column: any) => void;
    chevron: (column: any) => React.ReactNode;
  }) {
    const key = column.name || index;

    const style = {
      width: column.width || 'auto',
      textAlign: 'left' as const,
    };

    if (column.value === 'checkboxFault') {
      return (
        <HeaderCell
          key={key}
          style={{ ...style, textAlign: 'start' }}
          variant={variant}
        >
          <Checkbox
            key="checkbox-all-faults"
            checked={isAllSelected || false}
            onClick={(e) => selectAllCheckBox && selectAllCheckBox(!isAllSelected, e)}
          />
        </HeaderCell>
      );
    }

    if (column.checkable || column.sortable === false) {
      return (
        <HeaderCell
          key={key}
          style={{ ...style, textAlign: 'start' }}
          variant={variant}
        >
          {column.value}
        </HeaderCell>
      );
    }

    return (
      <HeaderCellOrder
        key={key}
        style={style}
        onClick={() => handleSetOrderColumn(column)}
        variant={variant}
      >
        {column.value}
        {chevron(column)}
      </HeaderCellOrder>
    );
  }

  return (
    <>
      <Flex flexWrap="wrap" flexDirection="row" width="100%" justifyContent="space-between">
        {!noSearchBar && (
          <Box minWidth="200px" width={[1, 1, 1, 1, 1 / 5]} mb={[16, 16, 16, 16, 16, 0]}>
            <InputSearch
              id={keySearch || 'search'}
              name="search"
              placeholder={t('pesquisar')}
              value={searchText}
              onChange={onSearch}
            />
          </Box>
        )}
        {
          (multipleConfig && profile.manageAllClients) && (
            <SearchInput
              style={{
                width: 300, margin: 0, marginRight: 20,
              }}
            >
              <div
                style={{
                  width: '100%',
                  paddingBottom: 4,
                  margin: 0,
                }}
              >
                <Label>{t('multiplasConfiguracoes')}</Label>
                <SelectSearch
                  options={[
                    {
                      value: 'setStatus',
                      name: t('definirStatus'),
                    },
                    {
                      value: 'setResponsible',
                      name: t('definirResponsavel'),
                    },
                    {
                      value: 'setWeeklyReport',
                      name: t('definirRelatorioSemanal'),
                    },
                  ]}
                  value={state.selectOption}
                  closeOnSelect
                  printOptions="on-focus"
                  search
                  filterOptions={fuzzySearch}
                  placeholder={`${state.checkedItems.length} ${t('selecionados')}`}
                  onChange={(value: any) => { setState({ selectOption: value }); }}
                />
              </div>
            </SearchInput>
          )
        }
        {extraBtns?.length !== 0 && (
          extraBtns?.map((btn) => <Box minWidth="200px" mb={[16, 16, 16, 16, 16, 0]}>{btn}</Box>)
        )}
      </Flex>
      <br />
      {!(hideEmptyTable && data.length === 0) && (
        <>
          <ResponsiveTable verifyMobile={verifyMobile} style={containerStyle} variant={variant}>
            <TableContainer verifyMobile={verifyMobile} style={tableStyle} variant={variant}>
              <TableHead>
                <HeaderRow>
                  {
                    (checkBox && profile.manageAllClients) && (
                      <CheckedAll filteredRows={filteredRows} state={state} AllChecked={(bool) => AllChecked(bool)} />
                    )
                  }
                  {columns.map((column, index) => renderHeaderCell({
                    column,
                    index,
                    variant,
                    handleSetOrderColumn,
                    chevron,
                  }))}
                  {!!actions && (
                    <HeaderCell style={{ width: '80px', textAlign: 'center' }} variant={variant}>
                      {t('acoes')}
                    </HeaderCell>
                  )}
                </HeaderRow>
              </TableHead>
              <TableBody variant={variant}>
                {getDataPerPage.map((row, index) => (
                  <Row key={index} variant={variant}>
                    {
                      (checkBox && profile.manageAllClients) && (
                        <Checked item={row} setFilter={toogleChecked} filteredRows={filteredRows} state={state} />
                      )
                    }
                    {columns.map((cell) => (
                      <DataCell
                        key={`${cell.name + index}`}
                        style={{
                          width: cell.width ?? 'auto',
                          textAlign: cell.textAlign as CSSProperties['textAlign'] ?? 'left',
                        }}
                        variant={variant}
                      >
                        {cell.render ? cell.render(row) : (cell.accessor ? row[cell.accessor] : '')}
                      </DataCell>
                    ))}
                    {!!actions && (
                      <DataCell key={`actions-${index}`} style={{ width: '80px' }}>
                        <Actions
                          row={row}
                          onDelete={actions.onDelete}
                          onEdit={actions.onEdit}
                        />
                      </DataCell>
                    )}
                  </Row>
                ))}
              </TableBody>
            </TableContainer>
          </ResponsiveTable>
          {
            styledPagination ? (
              <NewPagination
                currentPage={currentPage}
                onPageChange={handleChangePage}
                totalPages={Math.ceil((maxPages ?? filteredRows.length) / pageSize)}
              />
            ) : (
              <Pagination
                currentPage={currentPage}
                onNextPage={onNextPage}
                onPreviousPage={onPreviousPage}
                filteredRows={filteredRows}
                pageSize={pageSize}
              />
            )
          }
        </>
      )}
      {
        (state.selectOption === 'setStatus' || state.selectOption === 'setWeeklyReport' || state.selectOption === 'setResponsible') && (
          <ModalDefault
            state={state}
            setState={setState}
            t={t}
            supervisorList={supervisors}
            clientId={Number(clientId)}
            renderUnits={renderUnits}
            filteredRows={filteredRows}
          />
        )
      }
      {
        deleteUnitsOpen && (
          <ModalDeleteUnits
            filteredRows={filteredRows}
            onDeleteUnits={onDeleteUnits}
            onCloseDeleteUnits={onCloseDeleteUnits}
          />
        )
      }
    </>
  );
};

export const Pagination = ({
  currentPage,
  onPreviousPage,
  onNextPage,
  pageSize,
  filteredRows,
}) => (
  <PaginationContainer>
    <ButtonPage
      style={{
        borderTopLeftRadius: 10,
        borderBottomLeftRadius: 10,
        backgroundColor: currentPage > 1 ? colors.Blue300 : colors.Grey,
      }}
      onClick={onPreviousPage}
    >
      <ChevronLeft />
    </ButtonPage>
    <Page>{currentPage}</Page>
    <ButtonPage
      style={{
        borderTopRightRadius: 10,
        borderBottomRightRadius: 10,
        backgroundColor: currentPage * pageSize < filteredRows.length ? colors.Blue300 : colors.Grey,
      }}
      onClick={onNextPage}
    >
      <ChevronRight />
    </ButtonPage>
  </PaginationContainer>
);

function ModalDefault({
  state, setState, t, supervisorList, clientId, renderUnits, filteredRows,
}) {
  const [selected, setSelected] = useState<any>();
  const [loading, setLoading] = useState(false);

  const allOfOption = {
    setStatus: {
      name: t('alterarStatusUnidade'),
      label: t('status'),
      placeholder: t('selecionarStatus'),
      options: [
        {
          name: 'Em Instalação',
          value: 'installation',
        },
        {
          name: 'Em Produção',
          value: 'operation',
        },
      ],
      multiple: false,
    },
    setWeeklyReport: {
      name: t('definirRelatorioSemanal'),
      label: t('relatorioSemanal'),
      placeholder: t('selecionarHabilitacao'),
      options: [
        {
          name: 'Habilitar',
          value: 'enable',
        },
        {
          name: 'Desabilitar',
          value: 'disable',
        },
      ],
      multiple: false,
    },
    setResponsible: {
      name: t('definirResponsavel'),
      label: t('responsavel'),
      placeholder: t('selecioneResponsavel'),
      options: (supervisorList || []).map((item) => ({ value: item.USER, name: `${item.NOME} ${item.SOBRENOME}` })),
      multiple: true,
    },
  };

  async function sendItems() {
    if (!selected && !state.selectedItemsList) {
      return;
    }
    setLoading(true);
    const itemsUnits = filteredRows.filter((item) => state.checkedItems.includes(item.UNIT_ID));
    const arrayPromisse = [] as (ApiResps['/clients/get-units-list'] & ({
      groups?: {}[]
      duts?: {}[]
      DACS_COUNT?: number
      DUTS_COUNT?: number
      DAMS_COUNT?: number
      DRIS_COUNT?: number
      disp?: number
    })[]);
    for (let i = 0; i < itemsUnits.length; i += 60) {
      arrayPromisse.push(itemsUnits.slice(i, i + 60));
    }
    await Promise.all(
      arrayPromisse.map((arr) => apiCall('/clients/multiple-configs-units', {
        UNITS_IDS: itemsUnits.map((item) => ({ UNIT_ID: item.UNIT_ID, SUPERVISOR_ID: item.SUPERVISOR_ID })),
        CLIENT_ID: clientId,
        TYPE: state.selectOption,
        SELECTED: !selected ? 'clear' : selected,
      })),
    ).then(() => {
      toast.success(t('unidadesAlteradasComSucesso'));
      renderUnits();
      setState({ selectOption: '' });
    }).catch((err) => {
      console.log(err);
      toast.error(t('houveErro'));
      setState({ selectOption: '' });
    });
    setLoading(false);
  }

  return (
    <ModalWindow borderTop style={{ width: 400 }}>
      <h3>
        <strong style={{ marginLeft: 20 }}>{allOfOption[state.selectOption].name}</strong>
      </h3>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: 10,
        padding: 20,
      }}
      >
        <SearchInput style={{ margin: 0, zIndex: 10 }}>
          <div style={{ width: '100%', paddingTop: 3, paddingBottom: 3 }}>
            <Label>{allOfOption[state.selectOption].label}</Label>
            <SelectSearch
              options={allOfOption[state.selectOption].options}
              value={selected}
              printOptions="on-focus"
              search
              filterOptions={fuzzySearch}
              multiple={allOfOption[state.selectOption].multiple}
              closeOnSelect={!allOfOption[state.selectOption].multiple}
              placeholder={allOfOption[state.selectOption].placeholder}
              // eslint-disable-next-line react/jsx-no-bind
              onChange={(value) => { setSelected(value); }}
            />
          </div>
        </SearchInput>
        {
          state.selectOption === 'setResponsible' && (
            <p
              style={{
                color: 'blue',
                width: '100%',
                textDecoration: 'underline',
                paddingLeft: 3,
              }}
              onClick={() => { setSelected('clear'); sendItems(); }}
            >
              {t('limparResponsavel')}
            </p>
          )
        }
        <Button variant={loading ? 'disabled' : 'primary'} onClick={() => { sendItems(); }}>{t('aplicar')}</Button>
        <p
          style={{ textDecoration: 'underline', color: '#6C6B6B' }}
          onClick={() => setState({ selectOption: '' })}
        >
          {t('cancelar')}
        </p>
      </div>
    </ModalWindow>
  );
}

function ModalDeleteUnits(params: {
  filteredRows: { UNIT_ID: number, checked: boolean }[],
  onDeleteUnits?: (units: { UNIT_ID: number; checked: boolean }[]) => Promise<void>;
  onCloseDeleteUnits?: () => void,
}) {
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const {
    onCloseDeleteUnits, onDeleteUnits, filteredRows,
  } = params;

  async function sendItems() {
    if (!onDeleteUnits || !onCloseDeleteUnits) return;
    const unitsToDelete = filteredRows?.filter((x) => x.checked);

    if (!unitsToDelete.length) {
      toast.warn(t('escolhaUnidadesParaExcluir'));
      return;
    }
    try {
      setLoading(true);
      await onDeleteUnits(unitsToDelete);
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  }

  return (
    <ModalWindow borderTop style={{ width: 400 }} onClickOutside={onCloseDeleteUnits}>
      <h3 style={{ textAlign: 'center' }}>
        <strong style={{ textTransform: 'uppercase' }}>{t('aviso')}</strong>
      </h3>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: 10,
        padding: '10px 20px',
      }}
      >
        <p style={{ fontSize: '15px', fontWeight: '500', margin: '0 22px' }}>{t('desejaProsseguirComExclusaoDasUnidadesSelecionadas')}</p>
        <Button variant={loading ? 'disabled' : 'primary'} onClick={() => { sendItems(); }}>{t('prosseguir')}</Button>
        <button
          type="button"
          style={{
            textDecoration: 'underline',
            color: '#6C6B6B',
            cursor: 'pointer',
            background: 'none',
            border: 'none',
            padding: 0,
          }}
          onClick={onCloseDeleteUnits}
        >
          {t('cancelar')}
        </button>
      </div>
    </ModalWindow>
  );
}

function Checked({
  item, setFilter, state,
}) {
  const [checked, setChecked] = useState(false);
  function toogleChecked() {
    setChecked(!checked);
    setFilter(item);
  }
  useEffect(() => {
    const object = state.checkedItems.find((id) => item.UNIT_ID === id);
    setChecked(object || false);
  }, [state.checkedItems]);
  return (
    <DataCell>
      <Checkbox
        checked={checked}
        onClick={() => {
          toogleChecked();
        }}
      />
    </DataCell>
  );
}

function CheckedAll({ filteredRows, state, AllChecked }) {
  function isChecked() {
    return !filteredRows.some((item) => !state.checkedItems.some((check) => check === item.UNIT_ID));
  }
  useEffect(() => {
  }, [state.checkedItems]);
  return (
    <CheckboxStyle
      style={{
        borderRadius: '10px 0 0 0',
        display: 'flex',
        alignItems: 'center',
        backgroundColor: '#363BC4',
        justifyContent: 'center',
      }}
    >
      <Checkbox
        checked={isChecked()}
        onClick={() => {
          isChecked() ? AllChecked(false) : AllChecked(true);
        }}
      />
    </CheckboxStyle>
  );
}

function Actions<TypeRow>({
  row,
  onDelete,
  onEdit,
} : Readonly<ActionProps<TypeRow>>): ReactElement | null {
  if (!onEdit && !onDelete) return null;

  return (
    <ActionsContainer>
      {!!onEdit && (
        <ActionButton
          type="button"
          variant="info-filled"
          onClick={() => onEdit(row)}
        >
          <Edit5Icon />
        </ActionButton>
      )}
      {!!onDelete && (
        <ActionButton
          type="button"
          variant="danger-filled"
          onClick={() => onDelete(row)}
        >
          <Trash3Icon />
        </ActionButton>
      )}
    </ActionsContainer>
  );
}
